import {useMemo, useCallback} from 'react';

// selector
import {useSelector} from 'react-redux';
import {
  istaticDataPickSelector,
  privatePropertySelector,
  propertyLayoutTitleSelector,
  sharedPropertySelector,
} from '@stencil/hotels-cg-reducer/src/details/selectors';

//Utils
import {getRandomInt, isValidArray} from '@stencil/hotels-utils/src/common-utils';

//Types
import {PrivateSpace} from '@stencil/hotels-cg-reducer/src/details/types';

// constants
import {PrivateSpaces, RoomOptions} from './propertyLayoutConstants.tsx';

interface SpaceIdIndexMapping {
  spaceId: string;
  startIndex: number;
  endIndex: number;
}

function usePropertyLayout() {
  const istaticDataPick = useSelector(istaticDataPickSelector);
  const privateSpacesData = useSelector(privatePropertySelector);
  const sharedSpacesData = useSelector(sharedPropertySelector);
  const propertyLayoutTitleText = useSelector(propertyLayoutTitleSelector);

  const roomType = useMemo(() => {
    const propertyData = istaticDataPick
      ? privateSpacesData?.[0]?.spaces
      : privateSpacesData?.[0]?.privateSpaces?.spaces;
    return propertyData?.length > 1 ? PrivateSpaces : RoomOptions;
  }, [privateSpacesData]);

  const privateSpaces = useMemo(() => {
    return privateSpacesData?.flatMap(eachSpace => {
      const eachSpaceDetail = istaticDataPick
        ? eachSpace?.spaces
        : eachSpace?.privateSpaces?.spaces;
      if (eachSpaceDetail?.length && eachSpaceDetail.length > 1) {
        return eachSpaceDetail.flatMap(eachExactRoom => eachExactRoom || []);
      } else {
        return isValidArray(eachSpaceDetail) ? eachSpaceDetail[0] : [];
      }
    });
  }, [privateSpacesData]);

  const sharedSpaces = useMemo(() => {
    return sharedSpacesData?.map(d => ({...d, id: getRandomInt(10, 500), isCommonSpace: true}));
  }, [sharedSpacesData]);

  const isPropertyLayoutExist = privateSpaces?.length || sharedSpaces?.length;

  const onlyOneProperty = useMemo(
    () => privateSpaces?.length == 1 && sharedSpaces?.length == 0,
    [privateSpaces, sharedSpaces],
  );

  const combinedSpaces = useMemo(
    () => [...privateSpaces, ...sharedSpaces],
    [privateSpaces, sharedSpaces],
  ) as PrivateSpace[];

  const getTotalPhotosAndCount = useCallback(() => {
    if (!isValidArray(combinedSpaces)) return null;

    const imageCollection = [];
    let count = 0;
    for (let i = 0; i < combinedSpaces?.length; i++) {
      if (!isValidArray(combinedSpaces[i]?.media)) continue;
      imageCollection.push(...combinedSpaces[i]?.media);
      count += combinedSpaces[i].media.length;
    }

    return {
      allPhotos: imageCollection.map(imageObj => imageObj?.url),
      count,
    };
  }, [combinedSpaces]);

  const spaceIdIndexMapping = useMemo((): SpaceIdIndexMapping[] => {
    let currentIndex = 0;
    const mapping: SpaceIdIndexMapping[] = [];

    if (!isValidArray(combinedSpaces)) return [];

    for (const space of combinedSpaces) {
      if (!isValidArray(space?.media)) continue;
      const endIndex = currentIndex + space.media.length - 1;
      mapping.push({
        spaceId: space.spaceId,
        startIndex: currentIndex,
        endIndex,
      });
      currentIndex = endIndex + 1;
    }

    return mapping;
  }, [combinedSpaces]);

  const getSpaceDataForIndex = useCallback(
    (index: number): PrivateSpace | null => {
      if (!isValidArray(spaceIdIndexMapping)) return null;
      for (const mapping of spaceIdIndexMapping) {
        if (index >= mapping.startIndex && index <= mapping.endIndex) {
          const spaceData = combinedSpaces.find(space => space.spaceId === mapping.spaceId);
          if (spaceData) {
            return spaceData;
          }
        }
      }

      return null;
    },
    [spaceIdIndexMapping],
  );

  return {
    isPropertyLayoutExist,
    combinedSpaces,
    spaceIdIndexMapping,
    getTotalPhotosAndCount,
    getSpaceDataForIndex,
    privateSpaces: onlyOneProperty ? [] : privateSpaces, // dont show property layout section on getting single space
    sharedSpaces,
    roomType,
    propertyLayoutTitleText,
  };
}

// Export statement
export default usePropertyLayout;
